import React, { Component } from 'react';
import { withErrorBoundary } from '@/utils/errors';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Router from 'next/router';

import styles from './ContactUs.module.scss';

import FormValidator from '@/components/Forms/Validator';
import BottomSidebar from '@/components/BottomSidebar';
import HtmlEditor from '@/components/Helpers/HTMLEditor';

import { publish } from '@/utils/publish';
import { sendHelpQuestion } from '@/store/actions/admin/users.actions';
import { toPhoneFormat } from '@/utils/helpers';

class ContactUs extends Component {
  initState = {
    helpForm: {
      user_name: '',
      email: '',
      phone: '',
      comment: ''
    },
    changed: false
  };

  state = {
    ...this.initState
  };

  hasError = (formName, inputName, method) => {
    return (
      this.state[formName] &&
      this.state[formName].errors &&
      this.state[formName].errors[inputName] &&
      this.state[formName].errors[inputName][method]
    );
  };

  validateOnChange = (event, additionalParams = {}) => {
    const input = event.target;
    const form = input.form;
    let value = input.type === 'checkbox' ? input.checked : input.value;

    if (additionalParams.withPhoneFormat) {
      value = toPhoneFormat(value);
    }

    const result = FormValidator.validate(input);

    this.setState({
      [form.name]: {
        ...this.state[form.name],
        [input.name]: value,
        errors: {
          ...this.state[form.name].errors,
          [input.name]: result
        }
      },
      changed: true
    });
  };

  handleEditorChange = comment => {
    this.setState({
      helpForm: { ...this.state.helpForm, comment },
      changed: true
    });
  };

  onReset = async () => {
    await tinyMCE.activeEditor.setContent('');
    this.setState({ ...this.initState });
  };

  onSubmit = async (e, callback) => {
    const form = e.target.form;
    const inputs = [...form.elements].filter(i => ['INPUT'].includes(i.nodeName));
    const { errors, hasError } = FormValidator.bulkValidate(inputs);

    e.preventDefault();
    if (hasError) {
      this.setState({
        [form.name]: {
          ...this.state[form.name],
          errors
        }
      });

      callback();
      return;
    }

    const { user_name, email, phone, comment } = this.state.helpForm;

    await this.props
      .sendHelpQuestion({ name: user_name, email, phone, comment })
      .then(this.onReset)
      .then(() => publish('success', 'Your comment was successfully sent.'))
      .then(() => (this.props.login ? Router.push('/login') : Router.push('/')))
      .catch(res => publish('error', res));

    callback();
  };

  render() {
    const { user_name, email, phone, comment } = this.state.helpForm;
    const { login } = this.props;

    return (
      <div className={styles.wrapper}>
        <h5>Please complete the form below and we will assist you shortly</h5>

        <div className="columns">
          <div className="column">
            <form autoComplete="off" name="helpForm">
              <div className={classNames(styles.field, 'field')}>
                <div className="control">
                  <label htmlFor="user_name">
                    <p className={classNames(styles.title, 'title')}>Name</p>
                  </label>
                  <input
                    onChange={this.validateOnChange}
                    name="user_name"
                    className="input is-large"
                    type="text"
                    value={user_name}
                  />
                </div>
              </div>
              <div className={styles.flexedFields}>
                <div className={classNames(styles.field, 'field')}>
                  <div className="control">
                    <label className="required-field" htmlFor="email">
                      <p className="astericks">*</p>
                      <p className={classNames(styles.title, 'title')}>Email</p>
                    </label>
                    <input
                      id="email"
                      className={classNames('input is-large', {
                        'is-danger':
                          this.hasError('helpForm', 'email', 'email') || this.hasError('userForm', 'email', 'required')
                      })}
                      data-validate='["required", "email"]'
                      onChange={this.validateOnChange}
                      name="email"
                      type="email"
                      value={email}
                    />
                    {this.hasError('helpForm', 'email', 'required') && (
                      <p className="help is-danger">Field is required</p>
                    )}
                    {email && this.hasError('helpForm', 'email', 'email') && (
                      <p className="help is-danger">Invalid email address format</p>
                    )}
                  </div>
                </div>
                <div className={classNames(styles.field, 'field')}>
                  <div className="control">
                    <label htmlFor="phone">
                      <p className={classNames(styles.title, 'title')}>Phone</p>
                    </label>
                    <input
                      className="input is-large"
                      id="phone"
                      name="phone"
                      onChange={e => this.validateOnChange(e, { withPhoneFormat: true })}
                      value={phone}
                      maxLength={17}
                    />
                  </div>
                </div>
              </div>
              <div className={classNames(styles.field, 'field')}>
                <div className="control">
                  <label htmlFor="comment">
                    <p className={classNames(styles.title, 'title')}>Comment</p>
                  </label>
                  <div className="custom-signature email-management">
                    <HtmlEditor
                      imageDisabled={login}
                      initialValue={comment}
                      handleEditorChange={this.handleEditorChange}
                    />
                  </div>
                </div>
              </div>
              {
                <BottomSidebar
                  submitText="Submit"
                  resetEnable={this.state.changed}
                  onReset={this.onReset}
                  onCancel={() => (login ? Router.push('/login') : Router.push('/'))}
                  disabledSubmit={!(user_name || email || phone || comment)}
                  onSubmit={this.onSubmit}
                />
              }
            </form>
          </div>
          <div className="column is-is-one-fifth mt-5">
            <div className={classNames(styles.ContactInfo)}>
              <h3 className={classNames(styles.ContactInfoTitle)}> Contact Us</h3>

              <table className={classNames(styles.ContactInfoTable)}>
                <tr>
                  <td>
                    <p className={classNames(styles.ContactInfoText, styles.ContactInfoSubTitle)}>Phone:</p>
                  </td>
                  <td>
                    <p className={classNames(styles.ContactInfoText)}>
                      <a href="tel://18565770032">1-856-577-0032 x111</a>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className={classNames(styles.ContactInfoText, styles.ContactInfoSubTitle)}>Email:</p>
                  </td>
                  <td>
                    <p className={classNames(styles.ContactInfoText)}>
                      <a href="mailto://support@marketaccessdirect.com">support@marketaccessdirect.com</a>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className={classNames(styles.ContactInfoText, styles.ContactInfoSubTitle)}>Address:</p>
                  </td>
                  <td>
                    <p className={classNames(styles.ContactInfoText)}>
                      2 Executive Drive, Suite 123 <br />
                      Marlton, NJ 08053
                    </p>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({});

export default connect(mapStateToProps, { sendHelpQuestion })(withErrorBoundary(ContactUs));
